import { Link, Navigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowUpRight, MapPin } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import { Ridgeline } from "@/components/common/Ridgeline";
import { VideoEmbed } from "@/components/rentals/VideoEmbed";
import {
  getCommunity,
  EDMONTON_COMMUNITIES,
  CALGARY_COMMUNITIES,
} from "@/data/communities";
import { LISTINGS as RENTAL_LISTINGS } from "@/data/rentals";
import { listProperties, money } from "@/lib/properties";

/**
 * /neighbourhoods/:slug — unified detail template for BOTH cities.
 * Edmonton communities render the full editorial spread. Calgary
 * communities (pending: true) render a graceful "full write-up coming"
 * state while still showing the marketing overview + available rentals.
 */
export default function NeighbourhoodDetail() {
  const { slug } = useParams();
  const c = getCommunity(slug);
  if (!c) return <Navigate to="/neighbourhoods" replace />;

  const cityLabel = c.city === "calgary" ? "Calgary" : "Edmonton";
  const nameTokens = c.name
    .split("/")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const listings = RENTAL_LISTINGS.filter((l) =>
    nameTokens.some((t) => l.neighbourhood.toLowerCase().includes(t)),
  );

  const sameCityOthers = (
    c.city === "calgary" ? CALGARY_COMMUNITIES : EDMONTON_COMMUNITIES
  )
    .filter((n) => n.slug !== c.slug)
    .slice(0, 4);

  return (
    <div data-testid={`page-neighbourhood-${c.slug}`}>
      {/* ============ HERO ============ */}
      <header
        className="relative overflow-hidden"
        style={{ backgroundColor: "#24382E" }}
        data-nav-theme="dark"
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${c.hero})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.4,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,35,27,0.6) 0%, rgba(20,35,27,0.45) 55%, #24382E 100%)",
          }}
        />
        <div className="relative mx-auto max-w-[1440px] px-6 pb-24 pt-[140px] md:px-10 md:pb-32 md:pt-[172px]">
          <Reveal>
            <Link
              to="/neighbourhoods"
              className="group inline-flex items-center gap-2"
              style={{ color: "rgba(237,233,224,0.75)" }}
              data-testid="back-to-hub"
            >
              <ArrowLeft size={16} />
              <span className="label-caps text-[11px]">
                All neighbourhoods
              </span>
            </Link>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 items-end gap-8 md:grid-cols-12">
            <div className="md:col-span-9">
              <Reveal delay={0.05}>
                <Eyebrow color="#C89A4E">
                  {cityLabel}
                  {c.formerly ? ` · ${c.formerly}` : ""}
                </Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h1
                  className="font-display mt-6"
                  style={{
                    fontSize: "clamp(48px, 8vw, 128px)",
                    color: "#F8F5EF",
                    lineHeight: 0.9,
                    letterSpacing: "-0.02em",
                  }}
                  data-testid="neighbourhood-title"
                >
                  {c.name}
                </h1>
              </Reveal>
              <Reveal delay={0.12}>
                <p
                  className="body-measure mt-6 text-[17px] md:text-[19px]"
                  style={{ color: "rgba(237,233,224,0.85)" }}
                >
                  {c.vibe}
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-3 md:text-right">
              <Reveal delay={0.16}>
                <p
                  className="label-caps text-[10px]"
                  style={{ color: "rgba(174,196,199,0.75)" }}
                >
                  Rent range
                </p>
                <p
                  className="font-display mt-2"
                  style={{
                    fontSize: 32,
                    color: "#E3B268",
                    lineHeight: 1,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {c.rent}
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-5 flex flex-wrap gap-1.5 md:justify-end">
                  {c.bestFor.map((t) => (
                    <span
                      key={t}
                      className="label-caps rounded-full px-2.5 py-1 text-[10px]"
                      style={{
                        backgroundColor: "rgba(237,233,224,0.14)",
                        color: "#EDE9E0",
                      }}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </header>

      <HomesForRentHere slug={c.slug} />

      {c.pending ? (
        /* ============ PENDING STATE (Calgary, for now) ============ */
        <section
          style={{ backgroundColor: "#F8F5EF" }}
          data-nav-theme="light"
          data-testid="neighbourhood-pending"
        >
          <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
            <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
              <div className="md:col-span-5">
                <Reveal>
                  <Eyebrow>Full write-up coming</Eyebrow>
                </Reveal>
                <Reveal delay={0.05}>
                  <h2
                    className="font-display mt-6"
                    style={{
                      fontSize: "clamp(30px,4vw,52px)",
                      color: "#201E1A",
                      lineHeight: 1.02,
                    }}
                  >
                    Our Scouts are still{" "}
                    <span className="italic" style={{ color: "#C89A4E" }}>
                      walking the block.
                    </span>
                  </h2>
                </Reveal>
              </div>
              <div className="md:col-span-6 md:col-start-7">
                <Reveal delay={0.1}>
                  <p
                    className="text-[17px] leading-[1.7]"
                    style={{ color: "rgba(32,30,26,0.78)" }}
                  >
                    We only publish honest reads for neighbourhoods
                    we&rsquo;ve genuinely lived, worked, and rented in. The
                    full editorial write-up for {c.name} — including our
                    locals&rsquo; picks, commute times and the honest
                    trade-off — is in the queue.
                  </p>
                </Reveal>
                <Reveal delay={0.14}>
                  <p
                    className="mt-4 text-[17px] leading-[1.7]"
                    style={{ color: "rgba(32,30,26,0.78)" }}
                  >
                    In the meantime, {listings.length > 0
                      ? "here are the ROAM homes on the list in this area."
                      : "have a look at the current homes on our Calgary rentals page — new ones land most weeks."}
                  </p>
                </Reveal>
                <Reveal delay={0.18}>
                  <div className="mt-8 flex flex-wrap items-center gap-4">
                    <Link
                      to="/rentals/calgary"
                      className="pill pill-pine text-[15px]"
                      data-testid="pending-browse-calgary"
                    >
                      Browse Calgary rentals
                    </Link>
                    <Link
                      to="/contact"
                      className="group inline-flex items-center gap-2 text-[14px]"
                      style={{ color: "#24382E" }}
                    >
                      <span className="label-caps text-[12px]">
                        Ask us anything about {c.name}
                      </span>
                      <ArrowUpRight size={16} />
                    </Link>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* ============ WALKTHROUGH ============ */}
          <section
            style={{ backgroundColor: "#EDE9E0" }}
            data-nav-theme="light"
          >
            <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
              <Reveal>
                <Eyebrow>Walk it with us</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h2
                  className="font-display mt-6 max-w-[16ch]"
                  style={{
                    fontSize: "clamp(30px, 4vw, 52px)",
                    color: "#201E1A",
                    lineHeight: 1.02,
                  }}
                >
                  A slow lap of{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    {c.name.split(" / ")[0]}.
                  </span>
                </h2>
              </Reveal>
              <Reveal delay={0.15} className="mt-10">
                <VideoEmbed
                  url={c.walkVideoUrl}
                  title={`${c.name} walkthrough`}
                />
              </Reveal>
            </div>
          </section>

          {/* ============ HONEST READ ============ */}
          <section
            style={{ backgroundColor: "#F8F5EF" }}
            data-nav-theme="light"
          >
            <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
              <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
                <div className="md:col-span-4">
                  <Reveal>
                    <Eyebrow>The honest read</Eyebrow>
                  </Reveal>
                  <Reveal delay={0.05}>
                    <h2
                      className="font-display mt-6"
                      style={{
                        fontSize: "clamp(30px, 4vw, 52px)",
                        color: "#201E1A",
                        lineHeight: 1.02,
                      }}
                    >
                      Who thrives here.{" "}
                      <span className="italic" style={{ color: "#C89A4E" }}>
                        Who won&rsquo;t.
                      </span>
                    </h2>
                  </Reveal>
                </div>
                <div className="md:col-span-7 md:col-start-6">
                  <Reveal>
                    <div
                      className="border-t pt-6"
                      style={{ borderColor: "rgba(32,30,26,0.14)" }}
                    >
                      <p
                        className="label-caps text-[10px]"
                        style={{ color: "#C89A4E" }}
                      >
                        Thrives here
                      </p>
                      <p
                        className="mt-3 text-[17px] leading-[1.65]"
                        style={{ color: "rgba(32,30,26,0.82)" }}
                      >
                        {c.honestRead.thrives}
                      </p>
                    </div>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <div
                      className="mt-8 border-t pt-6"
                      style={{ borderColor: "rgba(32,30,26,0.14)" }}
                    >
                      <p
                        className="label-caps text-[10px]"
                        style={{ color: "#C89A4E" }}
                      >
                        Won&rsquo;t love it
                      </p>
                      <p
                        className="mt-3 text-[17px] leading-[1.65]"
                        style={{ color: "rgba(32,30,26,0.82)" }}
                      >
                        {c.honestRead.wont}
                      </p>
                    </div>
                  </Reveal>
                  <Reveal delay={0.15}>
                    <div
                      className="mt-10 border-l pl-6"
                      style={{ borderColor: "#C89A4E" }}
                    >
                      <p
                        className="label-caps text-[10px]"
                        style={{ color: "#C89A4E" }}
                      >
                        The trade-off
                      </p>
                      <p
                        className="font-display mt-3 italic"
                        style={{
                          fontSize: "clamp(20px,2.4vw,28px)",
                          color: "#24382E",
                          lineHeight: 1.25,
                        }}
                      >
                        {c.honestRead.tradeoff}
                      </p>
                    </div>
                  </Reveal>
                </div>
              </div>
            </div>
          </section>

          {/* ============ LOCALS' PICKS ============ */}
          <section
            style={{ backgroundColor: "#EDE9E0" }}
            data-nav-theme="light"
          >
            <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
              <Reveal>
                <Eyebrow>Locals&rsquo; picks</Eyebrow>
              </Reveal>
              <dl className="mt-10">
                {[
                  { k: "Coffee", v: c.picks.coffee },
                  { k: "Green space", v: c.picks.green },
                  { k: "Groceries", v: c.picks.groceries },
                  { k: "Saturday", v: c.picks.saturday },
                ].map((row, i) => (
                  <Reveal key={row.k} delay={i * 0.04}>
                    <div
                      className="grid grid-cols-[130px,1fr] items-baseline gap-4 py-5 md:grid-cols-[220px,1fr] md:gap-8 md:py-6"
                      style={{ borderTop: "1px solid rgba(32,30,26,0.14)" }}
                    >
                      <dt
                        className="label-caps text-[11px]"
                        style={{ color: "rgba(32,30,26,0.55)" }}
                      >
                        {row.k}
                      </dt>
                      <dd
                        className="text-[17px] leading-[1.5] md:text-[19px]"
                        style={{ color: "#201E1A" }}
                      >
                        {row.v}
                      </dd>
                    </div>
                  </Reveal>
                ))}
              </dl>
            </div>
          </section>

          {/* ============ GETTING AROUND ============ */}
          <section
            style={{ backgroundColor: "#F8F5EF" }}
            data-nav-theme="light"
          >
            <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-28">
              <Reveal>
                <Eyebrow>Getting around</Eyebrow>
              </Reveal>
              <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-10">
                {[
                  { k: "Downtown", v: c.commutes.downtown },
                  { k: "U of A / Hospital", v: c.commutes.uofa },
                  { k: "Airport", v: c.commutes.airport },
                ].map((row, i) => (
                  <Reveal
                    key={row.k}
                    delay={i * 0.04}
                    className={i % 2 === 1 ? "md:mt-10" : ""}
                  >
                    <div
                      className="border-t pt-5"
                      style={{ borderColor: "rgba(32,30,26,0.14)" }}
                    >
                      <p
                        className="label-caps text-[11px]"
                        style={{ color: "rgba(32,30,26,0.55)" }}
                      >
                        {row.k}
                      </p>
                      <p
                        className="font-display mt-3"
                        style={{
                          fontSize: "clamp(24px, 3vw, 34px)",
                          color: "#24382E",
                          lineHeight: 1.05,
                        }}
                      >
                        {row.v}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ============ LISTINGS ============ */}
      <section
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-32">
          <div className="flex items-end justify-between gap-6">
            <Reveal>
              <Eyebrow>In {c.name.split(" / ")[0]}</Eyebrow>
            </Reveal>
            {listings.length > 0 && (
              <Link
                to={`/rentals/${c.city || "edmonton"}`}
                className="label-caps hidden text-[11px] underline sm:inline-flex"
                style={{ color: "#24382E" }}
              >
                All {cityLabel} rentals
              </Link>
            )}
          </div>
          <Reveal delay={0.05}>
            <h2
              className="font-display mt-5"
              style={{
                fontSize: "clamp(30px, 4vw, 52px)",
                color: "#201E1A",
                lineHeight: 1.05,
              }}
            >
              Homes on the list
            </h2>
          </Reveal>

          {listings.length === 0 ? (
            <Reveal delay={0.05}>
              <div
                className="mt-10 flex flex-col items-start gap-6 rounded-[20px] p-8 md:flex-row md:items-center md:justify-between"
                style={{
                  backgroundColor: "#F8F5EF",
                  border: "1px solid rgba(32,30,26,0.08)",
                }}
                data-testid="neighbourhood-empty"
              >
                <p
                  className="font-display max-w-[38ch]"
                  style={{
                    fontSize: "clamp(22px,2.6vw,32px)",
                    color: "#201E1A",
                    lineHeight: 1.1,
                  }}
                >
                  Nothing available here right now — First Tracks members
                  hear first.
                </p>
                <Link
                  to="/relocating#firsttracks"
                  className="pill pill-pine text-[14px]"
                >
                  Join First Tracks
                </Link>
              </div>
            </Reveal>
          ) : (
            <ul className="mt-12 grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
              {listings.map((l, i) => (
                <Reveal key={l.slug} as="li" delay={i * 0.04}>
                  <Link
                    to={`/rentals/${l.city}/${l.type}/${l.slug}`}
                    className="group block"
                  >
                    <div
                      className="relative overflow-hidden"
                      style={{ aspectRatio: "4 / 5", borderRadius: 16 }}
                    >
                      <img
                        src={l.hero}
                        alt={l.name}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      />
                    </div>
                    <h3
                      className="font-display mt-4"
                      style={{ fontSize: 22, color: "#201E1A", lineHeight: 1.1 }}
                    >
                      {l.name}{" "}
                      <span style={{ color: "rgba(32,30,26,0.45)" }}>
                        {l.unit}
                      </span>
                    </h3>
                    <p
                      className="mt-1 inline-flex items-center gap-1.5 text-[13px]"
                      style={{ color: "rgba(32,30,26,0.6)" }}
                    >
                      <MapPin size={13} />
                      {l.neighbourhood} · ${l.monthlyRent.toLocaleString(
                        "en-CA",
                      )}/mo
                    </p>
                  </Link>
                </Reveal>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ============ ELSEWHERE ============ */}
      {sameCityOthers.length > 0 && (
        <section
          style={{ backgroundColor: "#F8F5EF" }}
          data-nav-theme="light"
        >
          <div className="mx-auto max-w-[1440px] px-6 py-24 md:px-10 md:py-28">
            <Reveal>
              <Eyebrow>Elsewhere in {cityLabel}</Eyebrow>
            </Reveal>
            <ul className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-4">
              {sameCityOthers.map((n, i) => (
                <Reveal key={n.slug} as="li" delay={i * 0.04}>
                  <Link
                    to={`/neighbourhoods/${n.slug}`}
                    className="group block p-5"
                    style={{
                      borderRadius: 18,
                      border: "1px solid rgba(32,30,26,0.1)",
                      backgroundColor: "#EDE9E0",
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className="font-display"
                        style={{ fontSize: 20, color: "#201E1A" }}
                      >
                        {n.name.split(" / ")[0]}
                      </p>
                      <ArrowUpRight
                        size={16}
                        color="#24382E"
                        className="mt-1 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </div>
                    <p
                      className="mt-2 text-[13px]"
                      style={{ color: "rgba(32,30,26,0.65)" }}
                    >
                      {n.vibe}
                    </p>
                  </Link>
                </Reveal>
              ))}
            </ul>
          </div>
          <div className="w-full opacity-70" aria-hidden>
            <Ridgeline stroke="#C89A4E" height={90} />
          </div>
        </section>
      )}
    </div>
  );
}

/* ============================================================
 * HomesForRentHere — Supabase-backed section injected just under
 * the hero. Renders nothing when zero properties match this
 * community_slug — the rest of the community page (editorial,
 * amenities, honest read) still stands on its own.
 * ============================================================ */
const HomesForRentHere = ({ slug }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    listProperties()
      .then((data) => {
        if (cancel) return;
        setItems(data.filter((p) => p.community_slug === slug));
      })
      .catch(() => {})
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [slug]);

  if (loading || items.length === 0) return null;

  return (
    <section
      style={{ backgroundColor: "#F8F5EF" }}
      data-nav-theme="light"
      data-testid="homes-for-rent-here"
    >
      <div className="mx-auto max-w-[1440px] px-6 py-16 md:px-10 md:py-24">
        <div className="flex items-end justify-between gap-6">
          <div>
            <Reveal>
              <p
                className="label-caps text-[11px]"
                style={{ color: "rgba(32,30,26,0.55)" }}
              >
                Homes for rent here
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2
                className="font-display mt-3"
                style={{
                  fontSize: "clamp(28px,3.4vw,44px)",
                  color: "#201E1A",
                  lineHeight: 1.05,
                }}
              >
                What&rsquo;s available in this neighbourhood.
              </h2>
            </Reveal>
          </div>
          <Link
            to="/residences"
            className="label-caps hidden text-[11px] md:inline-flex"
            style={{ color: "#8A6220" }}
          >
            All residences →
          </Link>
        </div>

        <ul className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p) => {
            const cover = p.hero_images_resolved?.[0];
            const price = (p.unit_types || [])
              .map((u) => u.rent_from)
              .filter(Boolean)
              .sort((a, b) => a - b)[0];
            return (
              <li key={p.id}>
                <Link
                  to={`/residences/${p.slug}`}
                  className="group block"
                  data-testid={`nh-property-${p.slug}`}
                >
                  <div
                    className="overflow-hidden"
                    style={{
                      borderRadius: 18,
                      aspectRatio: "4/3",
                      backgroundColor: "#EDE9E0",
                    }}
                  >
                    {cover && (
                      <img
                        src={cover}
                        alt={p.name}
                        className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-[1.04]"
                      />
                    )}
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div>
                      <p
                        className="font-display"
                        style={{ fontSize: 22, color: "#201E1A", lineHeight: 1.1 }}
                      >
                        {p.name}
                      </p>
                      <p
                        className="mt-1 text-[12px]"
                        style={{ color: "rgba(32,30,26,0.55)" }}
                      >
                        {p.tagline}
                      </p>
                    </div>
                    {price && (
                      <p
                        className="whitespace-nowrap text-[13px]"
                        style={{ color: "#8A6220" }}
                      >
                        From {money(price)}
                      </p>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

